import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageFrame } from '../../components/PageFrame';
import { SafeExitLink } from './SafeExitLink';
import type { createLearnerSessionClient } from '../../platform/learner/learner-session-client';

// S-SCR-008 Problem Report. Same minimalism as PausedState/EndEarlyState —
// no item content is fetched or rendered here, and the same
// never-claim-success-without-confirmation discipline as HelpState's
// "need an adult" flow: report_problem is only ever shown as recorded once
// the server has actually confirmed it.
//
// These four values are the literal payload strings
// record_session_action's SQL requires — not display labels. The display
// label is a plain-language, learner-facing paraphrase of each; the value
// sent to the server never changes based on wording choices here.
const CATEGORIES: readonly { value: string; label: string }[] = [
  { value: 'not_working', label: "Something isn't working" },
  { value: 'do_not_understand', label: "I don't understand what to do" },
  { value: 'need_an_adult', label: 'I need an adult' },
  { value: 'accessibility_problem', label: "I'm having trouble using this" },
];

// Category-appropriate, calm confirmation wording (need_an_adult's wording
// matches HelpState's own confirmation exactly, since it is the same
// underlying record — a deliberate, acceptable redundancy, not a
// duplicated implementation).
//
// UX audit: the earlier "This has been recorded so..." pattern reads
// clinical/surveillance-like four times across this screen and HelpState —
// same honest meaning ("your teacher is told"), warmer wording.
const CONFIRMATIONS: Record<string, string> = {
  not_working: "Your teacher will know something isn't working.",
  do_not_understand: 'Your teacher will know you need help understanding.',
  need_an_adult: 'Your teacher will know to check in with you.',
  accessibility_problem: 'Your teacher will know about this.',
};

const NOT_RECORDED_NOTE =
  'This might not have gone through — you can try again.';

type Props = {
  client: Pick<ReturnType<typeof createLearnerSessionClient>, 'sendAction'>;
};

export function ProblemReportState({ client }: Props) {
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [reportFailed, setReportFailed] = useState(false);
  const [confirmedCategory, setConfirmedCategory] = useState<string | null>(
    null,
  );

  async function handleReport() {
    if (selectedCategory === null) return;
    setSubmitting(true);
    setReportFailed(false);
    try {
      await client.sendAction(
        'report_problem',
        { category: selectedCategory },
        crypto.randomUUID(),
      );
      setSubmitting(false);
      // Only shown on genuine success — never claim something was
      // recorded when the call actually failed.
      setConfirmedCategory(selectedCategory);
    } catch {
      setSubmitting(false);
      setReportFailed(true);
    }
  }

  if (confirmedCategory !== null) {
    return (
      <PageFrame title="Report a problem" audience="learner">
        <p
          role="status"
          className="mt-4 rounded-xl border border-line-strong bg-paper p-4 text-ink-soft"
        >
          {CONFIRMATIONS[confirmedCategory]}
        </p>

        <div className="mt-6 grid gap-3 sm:flex sm:flex-wrap">
          <button
            type="button"
            className="dm-btn-primary"
            onClick={() => {
              // Pure navigation — nothing about the practice state changed,
              // so no server call is needed here.
              void navigate('/learn/practice', { replace: true });
            }}
          >
            Back to my task
          </button>
        </div>

        <SafeExitLink />
      </PageFrame>
    );
  }

  return (
    <PageFrame title="Report a problem" audience="learner">
      <p className="mt-4 text-lg leading-relaxed text-ink-soft">
        What kind of problem are you having?
      </p>

      {reportFailed && (
        <p role="alert" className="mt-4 text-sm text-muted">
          {NOT_RECORDED_NOTE}
        </p>
      )}

      <fieldset className="mt-6 grid gap-3">
        <legend className="sr-only">Choose a problem category</legend>
        {CATEGORIES.map((category) => (
          <label
            key={category.value}
            className="flex min-h-14 cursor-pointer items-center gap-3 rounded-xl border-[1.5px] border-line-strong bg-white px-4 font-semibold text-ink-soft transition-colors hover:border-forest-800 has-[:checked]:border-forest-800 has-[:checked]:bg-sage has-[:checked]:text-forest-950"
          >
            <input
              type="radio"
              name="problem-category"
              value={category.value}
              checked={selectedCategory === category.value}
              onChange={() => {
                setSelectedCategory(category.value);
              }}
            />
            {category.label}
          </label>
        ))}
      </fieldset>

      <div className="mt-6">
        <button
          type="button"
          disabled={submitting || selectedCategory === null}
          className="dm-btn-primary w-full sm:w-auto"
          onClick={() => {
            void handleReport();
          }}
        >
          Report this problem
        </button>
      </div>

      <SafeExitLink />
    </PageFrame>
  );
}
